import { useSearchConversationContent } from "@/hooks/useSearchConversationContent/useSearchConversationContent";
import { useTranscriptionRule } from "@/hooks/useTranscriptionRules/useTranscriptionRules";
import { TranscriptionMessage } from "../TranscriptionEmptyMessage";
import { TranscriptionWrongCallDuration } from "../TranscriptionEmptyMessage/components/TranscriptionWrongCallDuration";
import { TranscriptionFeedbackButtons } from "../TranscriptionFeedbackButtons/TranscriptionFeedbackButtons";
import { TranscriptionList } from "../TranscriptionList/TranscriptionList";
import { TranscriptionNotEnabledMessage } from "../TranscriptionNotEnabledMessage";
import { TranscriptionNotFound } from "../TranscriptionNotFound";

import { TranscriptionProps } from "./Transcription.decl";

import { useCall } from "@/hooks/useCall/useCall";
import { useCallDurationRule } from "@/hooks/useCallDurationRule/useCallDurationRule";
import { ScrollManagerProvider } from "@/providers/ScrollManagerContext/ScrollManagerProvider";
import { useIsFeedbackSent } from "@/states/feedbackSent";
import { Flex } from "@aircall/tractor";

import { useState } from "react";
import { AiPromotionalBanner } from "../AiPromotionalBanner/AiPromotionalBanner";
import { SummarySection } from "../SectionSummary";
import { TranscriptionLowConfidenceWithTranscript } from "../TranscriptionLowConfidenceWithTranscript/TranscriptionLowConfidenceWithTranscript";
import { TranscriptionReadDuration } from "../TranscriptionReadDuration/TranscriptionReadDuration";

export function Transcription({ mobile }: TranscriptionProps) {
  const { call } = useCall();

  const { isFeedbackSent } = useIsFeedbackSent();
  const { shouldDisplayCallDurationScreen } = useCallDurationRule();

  useSearchConversationContent();

  const {
    isTranscriptionDisabled,
    isTranscriptionAvailable,
    isLowConfidenceCall,
    isAvailableCall,
  } = useTranscriptionRule();

  const displayFeedbackButtons = isAvailableCall && !isFeedbackSent;

  const [showLowConfidence, setShowLowConfidence] = useState(
    isTranscriptionAvailable && isLowConfidenceCall
  );
  const handleClick = () => {
    setShowLowConfidence(false);
  };

  if (!call) {
    return null;
  }

  if (isTranscriptionDisabled) {
    return <TranscriptionNotEnabledMessage />;
  }

  if (shouldDisplayCallDurationScreen) {
    return <TranscriptionWrongCallDuration />;
  }

  if (!isTranscriptionAvailable) {
    // No usable transcription. A known reason (low confidence, bad acoustics, no
    // speech) has its own message; otherwise the transcription failed to load or was
    // never generated, so show the generic not-found empty state rather than blank. [CI-6183]
    if (!call.transcriptStatus) {
      return <TranscriptionNotFound />;
    }
    return <TranscriptionMessage status={call.transcriptStatus} />;
  }

  if (showLowConfidence) {
    return <TranscriptionLowConfidenceWithTranscript onClick={handleClick} />;
  }

  return (
    <ScrollManagerProvider mobile={mobile}>
      <Flex alignItems={"center"} mx="32px" mt="40px">
        <Flex flexDirection="column" maxW="640px" gap="l">
          <AiPromotionalBanner />
          <SummarySection />
          <TranscriptionReadDuration />
          <TranscriptionList />
          {displayFeedbackButtons && <TranscriptionFeedbackButtons />}
        </Flex>
      </Flex>
    </ScrollManagerProvider>
  );
}
